import { ResourceCache } from './ResourceCache';
import { ListingResource } from '../system/field';
import { BaseResourceManager } from './BaseResourceManager';
import { ResoResourceBase } from './types';

/**
 * Singleton registry for resource managers and their caches
 */
export class ResourceRegistry {
  private static instance: ResourceRegistry;
  private resourceManagers: Map<string, BaseResourceManager<any>> = new Map();
  private resourceCaches: Map<string, ResourceCache<any>> = new Map();

  private constructor() {}

  /**
   * Get the singleton instance
   */
  public static getInstance(): ResourceRegistry {
    if (!ResourceRegistry.instance) {
      ResourceRegistry.instance = new ResourceRegistry();
    }
    return ResourceRegistry.instance;
  }

  /**
   * Register a resource manager
   */
  public registerResourceManager<T extends ResoResourceBase>(
    resourceType: ListingResource | string,
    manager: BaseResourceManager<T>,
  ): void {
    this.resourceManagers.set(resourceType.toString(), manager);

    // Create a cache for this resource type if it doesn't exist
    if (!this.resourceCaches.has(resourceType.toString())) {
      this.resourceCaches.set(resourceType.toString(), new ResourceCache<T>());
    }
  }

  /**
   * Get a resource manager by type
   */
  public getResourceManager<T extends ResoResourceBase>(
    resourceType: ListingResource | string,
  ): BaseResourceManager<T> | undefined {
    return this.resourceManagers.get(resourceType.toString()) as
      | BaseResourceManager<T>
      | undefined;
  }

  /**
   * Get a resource cache by type, creating one if it doesn't exist
   */
  public getResourceCache<T extends ResoResourceBase>(
    resourceType: ListingResource | string,
  ): ResourceCache<T> {
    // Create a cache if it doesn't exist
    if (!this.resourceCaches.has(resourceType.toString())) {
      this.resourceCaches.set(resourceType.toString(), new ResourceCache<T>());
    }

    return this.resourceCaches.get(resourceType.toString()) as ResourceCache<T>;
  }

  /**
   * Invalidate a resource cache by type
   */
  public invalidateResourceCache(resourceType: ListingResource | string): void {
    const cache = this.resourceCaches.get(resourceType.toString());
    if (cache) {
      cache.invalidate();
    }
  }
}
